import { calculateOfferParts, OPTIONAL_PART_NOTE, type OfferPart, type OfferPartSettings } from "@/lib/offer-parts";
import { formatAmount, formatDecimal } from "@/lib/formatters";
import { OfferPartSelect } from "./offer-part-select";
import { offerPartsOverview } from "@/lib/offer-parts-overview";
import { useLayoutEffect, useRef, useState } from "react";

/** Keep the complete text on one line, condensing only when its cell is too small. */
function OverviewLine({ text, right = false }: { text: string; right?: boolean }) {
  const container = useRef<HTMLSpanElement>(null);
  const label = useRef<HTMLSpanElement>(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const fit = () => {
      const width = container.current?.clientWidth ?? 0;
      const natural = label.current?.scrollWidth ?? 0;
      if (width > 0 && natural > 0) setScale(Math.min(1, width / natural));
    };
    fit();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(fit) : null;
    if (container.current) observer?.observe(container.current);
    if (label.current) observer?.observe(label.current);
    window.addEventListener("resize", fit);
    return () => { observer?.disconnect(); window.removeEventListener("resize", fit); };
  }, [text]);
  return <span ref={container} className={`block overflow-hidden whitespace-nowrap ${right ? "text-right" : ""}`} title={text}>
    <span ref={label} className="inline-block" style={{ transform: `scaleX(${scale})`, transformOrigin: right ? "right" : "left" }}>{text}</span>
  </span>;
}

export interface OfferPreviewRow {
  id: string; description: string; quantity: number; unit: string; unitPrice: number; total: number;
  teil?: OfferPart; variant?: boolean; sourceLabel?: string;
  onTeilChange?: (part: OfferPart) => void;
}
export function OfferPartsPreview({ rows, settings }: { rows: OfferPreviewRow[]; settings: OfferPartSettings }) {
  const totals = calculateOfferParts(rows, settings);
  const overview = offerPartsOverview(totals);
  const money = (cents: number) => `${formatAmount(cents / 100)} €`;
  return <div className="space-y-8 p-4" data-testid="offer-parts-preview">
    {totals.parts.map((part) => <section key={part.id} className="space-y-3 border-b pb-6">
      <h5 className="font-bold text-bauwerk-teal">{part.title}</h5>
      {part.description?.trim() && <p className="whitespace-pre-wrap break-words text-xs leading-relaxed text-muted-foreground">{part.description}</p>}
      {!part.included && <p className="text-xs font-medium text-amber-800">{OPTIONAL_PART_NOTE}</p>}
      <div className="overflow-x-auto"><table className="w-full text-xs">
        <thead><tr className="border-b text-left text-muted-foreground">
          <th className="p-1">Leistung</th><th>Teil</th><th className="text-right">Menge</th><th className="p-1">Einh.</th><th className="text-right">EP €</th><th className="text-right">Gesamt €</th>
        </tr></thead>
        <tbody>{part.positions.map((row) => <tr key={row.id} className={`border-b ${row.variant ? "text-amber-800 italic" : ""}`}>
          <td className="p-1 whitespace-pre-wrap">{row.description}{row.variant && " (Variante)"}
            {row.sourceLabel && <small className="block text-muted-foreground">{row.sourceLabel}</small>}</td>
          <td><OfferPartSelect value={row.teil} settings={settings} onChange={(value) => row.onTeilChange?.(value)} label={`Angebotsteil für ${row.description}`} /></td>
          <td className="text-right tabular-nums">{formatDecimal(row.quantity, 6, { minFractionDigits: 0 })}</td>
          <td className="p-1">{row.unit}</td><td className="text-right tabular-nums">{formatAmount(row.unitPrice)}</td>
          <td className="text-right tabular-nums">{row.variant ? `(${formatAmount(row.total)})` : formatAmount(row.total)}</td>
        </tr>)}</tbody>
      </table></div>
      <p className="text-right text-sm font-semibold">Zwischensumme netto – {part.title}: {formatAmount(part.subtotal)} €</p>
    </section>)}
    <div className="w-full space-y-3" data-testid="offer-parts-summary">
      {overview.roundingNote && <p role="status" className="text-xs text-amber-800">{overview.roundingNote}</p>}
      <div>
        <h5 className="mb-2 text-sm font-semibold">Übersicht der Angebotsteile</h5>
        <table className="w-full table-fixed border-collapse text-sm" aria-label="Übersicht der Angebotsteile">
          <colgroup><col className="w-1/2" /><col className="w-1/6" /><col className="w-1/6" /><col className="w-1/6" /></colgroup>
          <thead><tr className="border-b bg-bauwerk-mint/20">
            <th className="px-[4mm] py-2 text-left font-normal"><OverviewLine text="Angebotsteil" /></th>
            <th className="px-[4mm] py-2 text-right font-normal"><OverviewLine text="Netto" right /></th>
            <th className="px-[4mm] py-2 text-right font-normal"><OverviewLine text="MwSt. 20 %" right /></th>
            <th className="px-[4mm] py-2 text-right font-normal"><OverviewLine text="Brutto" right /></th>
          </tr></thead>
          <tbody>
            {overview.rows.map((row, index) => <tr key={index} className={`border-b ${row.included ? "" : "text-gray-500"}`}>
              <td className="px-[4mm] py-2"><OverviewLine text={`${row.title}${!row.included ? " (optional)" : ""}`} /></td>
              <td className="px-[4mm] text-right tabular-nums"><OverviewLine text={money(row.net)} right /></td>
              <td className="px-[4mm] text-right tabular-nums"><OverviewLine text={money(row.tax)} right /></td>
              <td className="px-[4mm] text-right tabular-nums"><OverviewLine text={money(row.gross)} right /></td>
            </tr>)}
            <tr className="border-b font-bold"><td className="px-[4mm] py-2"><OverviewLine text="Summe" /></td>
              <td className="px-[4mm] text-right tabular-nums"><OverviewLine text={money(overview.sum.net)} right /></td>
              <td className="px-[4mm] text-right tabular-nums"><OverviewLine text={money(overview.sum.tax)} right /></td>
              <td className="px-[4mm] text-right tabular-nums"><OverviewLine text={money(overview.sum.gross)} right /></td>
            </tr>
          </tbody>
        </table>
      </div>
    <div className="w-full space-y-2 text-sm" data-testid="offer-parts-totals">
      <div className="space-y-2 rounded-lg border border-bauwerk-mint bg-[#fafcfc] px-[4mm] py-3">
        <div className="flex justify-between gap-4 font-semibold"><span>Gesamtsumme netto</span><span className="whitespace-nowrap tabular-nums">{formatAmount(totals.subtotal)} €</span></div>
        <div className="flex justify-between gap-4"><span>USt. 20 %</span><span className="whitespace-nowrap tabular-nums">{formatAmount(totals.tax)} €</span></div>
      </div>
      <div className="flex justify-between gap-4 rounded-lg bg-bauwerk-teal px-[4mm] py-3 font-bold text-white"><span>Gesamtbetrag brutto</span><span className="whitespace-nowrap tabular-nums">{formatAmount(totals.total)} €</span></div>
    </div>
    </div>
  </div>;
}
